import { useEffect, useCallback } from 'react';
import { useAppDispatch } from 'state/hooks';
import { addConsoleMessage } from 'state/slices/console-slice';

export const useConsoleCapture = () => {
  const dispatch = useAppDispatch();

  const addMessage = useCallback((type: 'log' | 'error' | 'warn' | 'info', args: any[]) => {
    const message = args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');

    dispatch(addConsoleMessage({
      type,
      message,
      stack: type === 'error' && args[0] instanceof Error ? args[0].stack : undefined,
    }));
  }, [dispatch]);

  useEffect(() => {
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
    };

    console.log = (...args) => {
      originalConsole.log(...args);
      addMessage('log', args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      addMessage('error', args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      addMessage('warn', args);
    };

    console.info = (...args) => {
      originalConsole.info(...args);
      addMessage('info', args);
    };

    const handleError = (event: ErrorEvent) => {
      addMessage('error', [`${event.error?.name || 'Error'}: ${event.message}`, event.error]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addMessage('error', ['Unhandled Promise Rejection:', event.reason]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      console.info = originalConsole.info;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addMessage]);

  // This hook now just sets up console capture globally
  // Messages are accessed via Redux selectors
};

// Export the ConsoleMessage interface for other components to use
export type { ConsoleMessage } from 'state/slices/console-slice'; 